import { useEffect, useRef, type MutableRefObject } from "react"

type TOptions<T> = {
	/** True while this entity's own saves are queued — a value changing meanwhile is skipped. */
	isSaving?: boolean
	isEqual?: (a: T, b: T) => boolean
}

/**
 * Resyncs a sidebar field seeded into local state from the server value in its (refetched) prop —
 * the pop-out's peer window reaches a sidebar only that way (#1430). Applies `incoming` when it
 * CHANGES to something other than `savedRef` (what this sidebar last saved), so its own commit echo
 * is a no-op; a change landing while `isSaving` (an EARLIER queued commit's echo) is skipped too.
 *
 * Reacts to `incoming` only — never to `isSaving` turning off: the queue drains a render BEFORE the
 * prop carries the save, so re-checking then compares the stale pre-save value and resets the field.
 * The cost is that a peer edit landing mid-save is dropped until the next change. Pass a memoized
 * `incoming` when it is an object.
 */
export const useServerResync = <T>(
	incoming: T,
	savedRef: MutableRefObject<T>,
	apply: (value: T) => void,
	{ isSaving = false, isEqual = Object.is }: TOptions<T> = {},
) => {
	const applyRef = useRef(apply)
	applyRef.current = apply
	const isEqualRef = useRef(isEqual)
	isEqualRef.current = isEqual
	const isSavingRef = useRef(isSaving)
	isSavingRef.current = isSaving

	useEffect(() => {
		if (isSavingRef.current || isEqualRef.current(incoming, savedRef.current)) return
		savedRef.current = incoming
		applyRef.current(incoming)
	}, [incoming, savedRef])
}
